"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { reportClientErrorToSupport } from "@/lib/client-error-report";

/** How many times the SAME error fingerprint can re-throw within RECENT_WINDOW_MS
 *  before we stop offering Try again (which would just re-mount and re-crash) and
 *  hard-reload the tab instead. Keeps users from being stuck on an error screen
 *  that keeps coming back on every reset. */
const REPEAT_THRESHOLD = 2;
const RECENT_WINDOW_MS = 8_000;
const RECENT_STORE_KEY = "jorvio.error-recent";

type RecentError = { fingerprint: string; firstAt: number; count: number };

function loadRecent(): RecentError | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(RECENT_STORE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RecentError;
    if (!parsed || typeof parsed.fingerprint !== "string") return null;
    if (Date.now() - parsed.firstAt > RECENT_WINDOW_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveRecent(entry: RecentError) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(RECENT_STORE_KEY, JSON.stringify(entry));
  } catch {
    /* private mode — give up; recovery still works, just no repeat-detection. */
  }
}

function clearRecent() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(RECENT_STORE_KEY);
  } catch {
    /* ignore */
  }
}

export default function AppError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  const sent = useRef(false);
  const router = useRouter();
  const fingerprint = `${error.digest ?? ""}|${(error.message || "").slice(0, 200)}`;
  const recent = loadRecent();
  const isRepeating =
    recent !== null && recent.fingerprint === fingerprint && recent.count + 1 >= REPEAT_THRESHOLD;

  useEffect(() => {
    if (sent.current) return;
    sent.current = true;

    /** Record this error so a subsequent crash in the same fingerprint trips the
     *  hard-reload fallback below instead of looping the user on a broken
     *  "Try again → re-crash → Try again" cycle (common with React #185 on mobile). */
    if (recent && recent.fingerprint === fingerprint) {
      saveRecent({ ...recent, count: recent.count + 1 });
    } else {
      saveRecent({ fingerprint, firstAt: Date.now(), count: 1 });
    }

    void reportClientErrorToSupport({
      kind: "react_error",
      message: error.message || "Unknown error",
      stack: error.stack,
      digest: error.digest,
    });
  }, [error, fingerprint, recent]);

  /**
   * Use the browser history when possible so "Go back" returns to the page the user
   * actually came from (e.g. /proposals when an open-quote click crashed). Falls back
   * to /dashboard only when there is no usable history (direct landing on the error
   * route). Avoids the previous behavior that always sent users to the home marketing
   * page even from inside the authenticated app.
   */
  function goBack() {
    clearRecent();
    if (typeof window === "undefined") return;
    try {
      const ref = document.referrer;
      const sameOriginRef = ref && new URL(ref).origin === window.location.origin;
      if (window.history.length > 1 && sameOriginRef) {
        router.back();
        return;
      }
    } catch {
      /* invalid referrer URL — fall through to dashboard */
    }
    router.push("/dashboard");
  }

  function tryAgain() {
    /** Same error already crashed us once in the last few seconds → reset would
     *  almost certainly crash again. Hard-reload bypasses the React state that
     *  triggered the loop in the first place. */
    if (isRepeating && typeof window !== "undefined") {
      clearRecent();
      window.location.reload();
      return;
    }
    reset();
  }

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-6">
      <h1 className="text-xl font-semibold">Something went wrong</h1>
      <p className="max-w-md text-center text-sm text-muted-foreground">
        {isRepeating
          ? "This page keeps crashing. Click Reload to refresh; that often fixes it."
          : "The app hit an unexpected error. Our team was notified. You can try again or go back to where you were."}
      </p>
      <div className="flex flex-wrap justify-center gap-2">
        <Button type="button" onClick={tryAgain}>
          {isRepeating ? "Reload page" : "Try again"}
        </Button>
        <Button type="button" variant="outline" onClick={goBack}>
          Go back
        </Button>
      </div>
    </div>
  );
}
